import { type ChangeEvent, Fragment, type ReactElement, useState } from "react"

import { useDropzone } from "react-dropzone"
import { useTranslation } from "react-i18next"
import { useMutation, useQuery } from "react-query"
import { useDispatch, useSelector } from "react-redux"

import * as Yup from "yup"
import { ImageOutlined } from "@mui/icons-material"
import { Button, InputText, NotificationUtils, Skeleton, TextArea, Typography } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"
import { useFormik } from "formik"

import { InfoBlock } from "../../../components/containers/InfoBlock"
import { SidAPI } from "../../../services/SidAPI"
import type { RootState } from "../../../store/ReduxStore"
import type { Organization } from "../../../types/generated/authentication/Organization"
import type { PatchedOrganization } from "../../../types/generated/authentication/PatchedOrganization"
import { getEnvVar, validateFileFormat, validateFileSize } from "../../../utils/genericHelpers"
import { setOrganizationName } from "../../Auth/Slice"
import { InfoRow } from "./InfoRow"

import styles from "./OrganizationForm.module.scss"

/**
 * `OrganizationForm` is a settings component for viewing and editing details about
 * the current organization, including logo, name, and description.
 *
 * Only users with the owner role can enter edit mode.
 *
 * @returns A ReactElement displaying editable organization information.
 */
export function OrganizationForm(): ReactElement {
  const { t } = useTranslation("settings")

  // Maximum file size for organization logo
  const maxFileSizeInMB = Number(getEnvVar("ORGANIZATION_PROFILE_PICTURE_MAX_SIZE"))
  const maxFileSize = maxFileSizeInMB * 1024 * 1024

  const [logo, setLogo] = useState<File | null>(null)
  const [isInEditMode, setIsInEditMode] = useState(false)

  const dispatch = useDispatch()
  const organizationId = useSelector((state: RootState) => state.auth.organizationId)
  const roleIsOwner = useSelector((state: RootState) => state.auth.roleIsOwner)

  // Fetching organization information
  const { isFetching: isOrganizationDataLoading, data: organizationData } = useQuery<
    AxiosResponse<Organization>,
    AxiosError
  >(["currentOrganization"], () => SidAPI.fetchCurrentOrganization(), {
    onSuccess: (response) => {
      formik.resetForm({
        values: {
          name: response.data.name,
          description: response.data.description ?? "",
          imgUrl: response.data.logo ?? null,
        },
      })
    },
    refetchOnMount: false,
  })

  const updateOrganizationMutation = useMutation<AxiosResponse<Organization>, AxiosError, PatchedOrganization>(
    SidAPI.updateOrganization,
    {
      onSuccess: (response) => {
        NotificationUtils.toast(t("organizationForm.updateOrganization.success"), { severity: "success" })
        dispatch(setOrganizationName(response.data.name))
        formik.resetForm({
          values: {
            name: response.data.name,
            description: response.data.description ?? "",
            imgUrl: response.data.logo ?? null,
          },
        })
        setIsInEditMode(false)
      },
      onError: () => {
        NotificationUtils.toast(t("organizationForm.updateOrganization.error"), { severity: "error" })
      },
    },
  )

  const formik = useFormik({
    initialValues: {
      name: organizationData?.data.name ?? "",
      description: organizationData?.data.description ?? "",
      imgUrl: organizationData?.data.logo ?? (null as string | undefined | null),
    },
    validationSchema: Yup.object({
      name: Yup.string().trim().min(1, "Organization name is required").required("Organization name is required"),
    }),
    onSubmit: (values) => {
      updateOrganizationMutation.mutate({
        uid: organizationId,
        name: values.name,
        description: values.description,
        logo: logo,
      })
    },
  })

  const onDrop = (acceptedFiles: File[]): void => {
    if (acceptedFiles.length === 0) return // Checks if there is an accepted file

    // delete the old object URL if it exists

    if (acceptedFiles[0]) {
      if (formik.values.imgUrl) {
        URL.revokeObjectURL(formik.values.imgUrl)
      }

      formik.setFieldValue("imgUrl", URL.createObjectURL(acceptedFiles[0]))

      setLogo(acceptedFiles[0])
    }
  }

  const { open, getRootProps, getInputProps } = useDropzone({
    maxSize: maxFileSize, // in bytes
    multiple: false,
    accept: {
      "image/*": [".png", ".jpeg"],
    },
    noClick: true,
    noKeyboard: true,
    onDrop,
    validator: (file) => {
      return validateFileFormat(file, [".png", ".jpg", ".jpeg"]) || validateFileSize(file, maxFileSizeInMB)
    },
  })

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <Typography variant="h2-bold">{t("organizationForm.title")}</Typography>
        {roleIsOwner &&
          (isInEditMode ? (
            <div className={styles.cancelSave}>
              <Button
                variant="ghost-secondary"
                size="small"
                disabled={updateOrganizationMutation.isLoading}
                onClick={() => {
                  formik.resetForm()
                  setIsInEditMode(false)
                }}
              >
                {t("buttons.cancel")}
              </Button>

              <Button
                variant="primary"
                size="small"
                disabled={updateOrganizationMutation.isLoading || !formik.dirty || !formik.isValid}
                isLoading={updateOrganizationMutation.isLoading}
                onClick={() => {
                  formik.handleSubmit()
                }}
              >
                {t("buttons.save")}
              </Button>
            </div>
          ) : (
            <Button variant="ghost" size="small" onClick={() => setIsInEditMode(true)}>
              {t("buttons.edit")}
            </Button>
          ))}
      </div>

      <InfoRow rowTitle={t("organizationForm.logo")}>
        {isOrganizationDataLoading ? (
          <Skeleton width={"320px"} height={"32px"} />
        ) : (
          <Fragment>
            <div>
              {formik.values.imgUrl ? (
                <img src={formik.values.imgUrl} alt={t("organizationForm.logoPlaceholder")} className={styles.logo} />
              ) : (
                <div className={styles.placeholder}>
                  <ImageOutlined className={styles.icon} />
                </div>
              )}

              <div {...getRootProps()}>
                <input {...getInputProps()} />
              </div>
            </div>

            {roleIsOwner && isInEditMode && (
              <div className={styles.cancelSave}>
                <Button
                  variant="ghost-secondary"
                  size="small"
                  disabled={!formik.values.imgUrl || updateOrganizationMutation.isLoading}
                  onClick={() => {
                    formik.setFieldValue("imgUrl", null)
                    setLogo(null)
                  }}
                >
                  {t("buttons.delete")}
                </Button>

                <Button
                  variant="ghost"
                  size="small"
                  onClick={() => {
                    if (!updateOrganizationMutation.isLoading) open()
                  }}
                  disabled={updateOrganizationMutation.isLoading}
                >
                  {!formik.values.imgUrl ? t("buttons.upload") : t("buttons.reupload")}
                </Button>
              </div>
            )}
          </Fragment>
        )}
      </InfoRow>

      <InfoRow rowTitle={t("organizationForm.organizationName")} className={styles.infoBlock}>
        {isOrganizationDataLoading ? (
          <Skeleton width={"320px"} height={"32px"} />
        ) : isInEditMode ? (
          <InputText
            fullWidth
            hideDescription
            variant={"outlined"}
            placeholder={t("organizationForm.organizationName")}
            id="name"
            disabled={updateOrganizationMutation.isLoading}
            value={formik.values.name}
            handleChange={formik.handleChange}
            error={formik.errors.name}
          />
        ) : (
          <InfoBlock text={formik.values.name ?? ""} type="default" />
        )}
      </InfoRow>

      <InfoRow rowTitle={t("organizationForm.description")}>
        {isOrganizationDataLoading ? (
          <Skeleton width={"320px"} height={"32px"} />
        ) : (
          <div className={styles.textArea}>
            <TextArea
              hideDescription
              className={styles.textArea}
              height={54}
              placeholder={t("organizationForm.description")}
              id="description"
              disabled={updateOrganizationMutation.isLoading}
              value={formik.values.description}
              readOnly={!isInEditMode}
              handleChange={(e: ChangeEvent) => {
                formik.setFieldValue("description", (e as ChangeEvent<HTMLTextAreaElement>).target.value as string)
              }}
            />
          </div>
        )}
      </InfoRow>
    </div>
  )
}
